export type PasswordRequirementId = "minLength" | "upper" | "lower" | "special" | "digit";

/** neutral = grå, met = grøn, unmet = rød (efter blur når krav mangler for gyldighed). */
export type PasswordRequirementVisualState = "neutral" | "met" | "unmet";

export interface PasswordRequirementState {
    id: PasswordRequirementId;
    label: string;
    satisfied: boolean;
    visualState: PasswordRequirementVisualState;
}

const MIN_LENGTH = 14;
const MIN_CHAR_CLASSES = 3;

const HAS_UPPER = /[A-ZÆØÅ]/;
const HAS_LOWER = /[a-zæøå]/;
const HAS_DIGIT = /\d/;
const HAS_SPECIAL = /[^A-Za-zÆØÅæøå0-9]/;

export function hasMinLength(password: string): boolean {
    return (password || "").length >= MIN_LENGTH;
}

export function hasUpperCase(password: string): boolean {
    return HAS_UPPER.test(password || "");
}

export function hasLowerCase(password: string): boolean {
    return HAS_LOWER.test(password || "");
}

export function hasDigit(password: string): boolean {
    return HAS_DIGIT.test(password || "");
}

export function hasSpecialChar(password: string): boolean {
    return HAS_SPECIAL.test(password || "");
}

export function countSatisfiedCharClasses(password: string): number {
    return [hasUpperCase(password), hasLowerCase(password), hasDigit(password), hasSpecialChar(password)].filter(Boolean).length;
}

/** Password er gyldigt når længde ≥ 14 og mindst 3 af 4 karakterkrav er opfyldt. */
export function isPasswordValid(password: string): boolean {
    return hasMinLength(password) && countSatisfiedCharClasses(password) >= MIN_CHAR_CLASSES;
}

export function passwordsMatch(password: string, repeat: string): boolean {
    return (password || "") !== "" && password === repeat;
}

/**
 * Evaluerer krav til UI-listen.
 * @param showUnmetAsError efter blur på adgangskode-feltet: manglende krav for gyldighed bliver røde.
 */
export function evaluatePasswordRequirements(password: string, showUnmetAsError: boolean): PasswordRequirementState[] {
    const lengthOk = hasMinLength(password);
    const upperOk = hasUpperCase(password);
    const lowerOk = hasLowerCase(password);
    const digitOk = hasDigit(password);
    const specialOk = hasSpecialChar(password);
    const classCount = [upperOk, lowerOk, digitOk, specialOk].filter(Boolean).length;
    const needsMoreClasses = classCount < MIN_CHAR_CLASSES;

    const toVisual = (satisfied: boolean, requiredForValidity: boolean): PasswordRequirementVisualState => {
        if (satisfied) {
            return "met";
        }
        if (showUnmetAsError && requiredForValidity) {
            return "unmet";
        }
        return "neutral";
    };

    return [
        {
            id: "minLength",
            label: "Mindst 14 tegn",
            satisfied: lengthOk,
            visualState: toVisual(lengthOk, true)
        },
        {
            id: "upper",
            label: "Store bogstaver",
            satisfied: upperOk,
            visualState: toVisual(upperOk, needsMoreClasses)
        },
        {
            id: "lower",
            label: "Små bogstaver",
            satisfied: lowerOk,
            visualState: toVisual(lowerOk, needsMoreClasses)
        },
        {
            id: "special",
            label: "Specialtegn",
            satisfied: specialOk,
            visualState: toVisual(specialOk, needsMoreClasses)
        },
        {
            id: "digit",
            label: "Tal",
            satisfied: digitOk,
            visualState: toVisual(digitOk, needsMoreClasses)
        }
    ];
}
